import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type {
  CustomerRegistrationInput, DishDto, PlanDto, RegistrationAcceptInput, RegistrationAcceptedDto, RegistrationRequestDto, RegistrationSubmittedDto, SubscriptionMealRules,
} from '@tiffinify/contracts';
import {
  computeEndDate, daysLabel, formatDay, formatPhone, mealRulesToSave, mealsOf, sameDays, slotOf, tiffinLines, tiffinsOf, tiffinsText, totalMeals, waLink,
  type Food, type Meal, type Slot,
} from '@tiffinify/core';
import {
  Badge, BottomNav, BtnPrimary, BtnSecondary, Card, CardTitle, DayPicker, DietPicker, Empty, ErrorText, Field, Helper, Hero, Icon, Logo, MealPicker, Mono, PageLoader, RadioCard, Section, TaskTitle, TopBar,
} from '../components/ui';
import { MealChoiceSection } from '../components/MealChoice';
import { ApiClientError, post, del } from '../lib/api';
import { errorMessage, formatInr, localPhone, mealName, mealsLabel, planUnit } from '../lib/format';
import { usePublicRegistration, useRegistrationInbox } from '../lib/queries';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fieldsFrom(error: unknown) {
  return error instanceof ApiClientError ? error.fields : {};
}

function submittedDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Lunch: 2× Paneer Bhurji + Egg special", one line per meal that has choices. */
function mealChoiceLines(rules: SubscriptionMealRules | null | undefined, food: Food): string[] {
  return Object.entries(rules ?? {}).flatMap(([m, rule]) => (rule ? [`${mealName(m as Meal)}: ${tiffinsText(tiffinLines(tiffinsOf(rule, food)))}`] : []));
}

export function RegistrationRequests() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const inbox = useRegistrationInbox();
  const [selectedId, setSelectedId] = useState('');
  const [copyNote, setCopyNote] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const selected = inbox.data?.requests.find((r) => r.id === selectedId) ?? inbox.data?.requests[0];

  const accept = useMutation({
    mutationFn: ({ id, ...body }: RegistrationAcceptInput & { id: string }) => post<RegistrationAcceptedDto>(`/registrations/${id}/accept`, body),
    onSuccess: async ({ subscriberId, endDate, emailed, welcome }) => {
      await qc.invalidateQueries();
      const first = selected?.name.split(' ')[0] ?? 'the customer';
      const mail = emailed === 'sent' ? ` We emailed ${first} the plan.` : ` The email to ${first} did not go out.`;
      nav(`/s/${subscriberId}`, {
        state: { flash: { text: `Accepted. The plan ends ${formatDay(endDate)}.${mail}`, ...(welcome ? { wa: { label: `Welcome ${first}`, link: welcome.link } } : {}) } },
      });
    },
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => del(`/registrations/${id}`),
    onSuccess: () => { setSelectedId(''); setStatusNote('Request dismissed.'); void qc.invalidateQueries({ queryKey: ['registration-inbox'] }); },
  });

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopyNote('Link copied.');
    } catch {
      setCopyNote('Could not copy. Press and hold the link to copy it.');
    }
  };
  const share = inbox.data ? waLink('', `Hi! Please register with ${inbox.data.kitchenName} here: ${inbox.data.link}`) : undefined;

  return (
    <>
      <main className="main task">
        <TopBar to="/subscribers" label="Back to subscribers" />
        <TaskTitle title="Customer registrations" sub="Share the link, then review each request and set the final plan." />

        <Card pad>
          <CardTitle title="Registration link" />
          {inbox.isPending ? <PageLoader label="Preparing your link" /> : inbox.data ? (
            <>
              <Field label="Share this link" id="registration-link" value={inbox.data.link} readOnly />
              <BtnSecondary label="Share on WhatsApp" icon="chat" href={share} />
              <BtnSecondary label="Copy link" icon="sheet" onClick={() => void copy(inbox.data!.link)} />
              {copyNote && <p className="st-ok" role="status">{copyNote}</p>}
            </>
          ) : <ErrorText>{errorMessage(inbox.error)}</ErrorText>}
        </Card>

        <Section title="Waiting for you" meta={inbox.data?.requests.length ? <Badge kind="due">{inbox.data.requests.length}</Badge> : undefined}>
          {inbox.isPending ? <PageLoader label="Loading requests" /> : inbox.data?.requests.length ? (
            <>
              <Card>
                {inbox.data.requests.map((r) => (
                  <RadioCard
                    key={r.id}
                    title={r.name}
                    sub={`${formatPhone(r.phone)}${r.area ? ` · ${r.area}` : ''}${r.plan ? ` · ${r.plan.name} from ${formatDay(r.plan.startDate)}` : ''} · sent ${submittedDate(r.submittedAt)}`}
                    right={mealChoiceLines(r.mealRules ?? r.plan?.mealRules, r.food).length ? 'Chose dishes' : undefined}
                    on={selected?.id === r.id}
                    onClick={() => { setSelectedId(r.id); setStatusNote(''); }}
                  />
                ))}
              </Card>
              {selected && (
                <ReviewRequest
                  key={selected.id}
                  request={selected}
                  plans={inbox.data.plans}
                  dishes={inbox.data.dishes}
                  deliveryDays={inbox.data.deliveryDays}
                  holidays={inbox.data.holidays}
                  servedMeals={inbox.data.servedMeals}
                  today={inbox.data.today}
                  onAccept={(v) => accept.mutate({ id: selected.id, ...v })}
                  onDismiss={() => dismiss.mutate(selected.id)}
                  busy={accept.isPending || dismiss.isPending}
                  error={accept.error ?? dismiss.error}
                />
              )}
            </>
          ) : (
            <Card><Empty title="Nothing to review">When a customer sends the form, their request shows up here.</Empty></Card>
          )}
        </Section>
        {statusNote && <p className="st-ok" role="status">{statusNote}</p>}
      </main>
      <BottomNav />
    </>
  );
}

function ReviewRequest({
  request, plans, dishes, deliveryDays, holidays, servedMeals, today, onAccept, onDismiss, busy, error,
}: {
  request: RegistrationRequestDto;
  plans: PlanDto[];
  dishes: DishDto[];
  deliveryDays: number[];
  holidays: string[];
  servedMeals: Meal[];
  today: string;
  onAccept: (v: RegistrationAcceptInput) => void;
  onDismiss: () => void;
  busy: boolean;
  error: unknown;
}) {
  const [name, setName] = useState(request.name);
  const [phone, setPhone] = useState(localPhone(request.phone));
  const [email, setEmail] = useState(request.email ?? '');
  const [area, setArea] = useState(request.area);
  const [food, setFood] = useState<Food>(request.food);
  const [notes, setNotes] = useState(request.notes);
  const availablePlan = request.plan?.id === 'custom' ? 'custom'
    : request.plan && plans.some((p) => p.id === request.plan?.id) ? request.plan.id : plans[0]?.id ?? 'custom';
  const [planId, setPlanId] = useState(availablePlan);
  const initialPlan = plans.find((p) => p.id === availablePlan);
  const servable = (s: Slot) => mealsOf(s).every((meal) => servedMeals.includes(meal));
  const [startDate, setStartDate] = useState(request.plan?.startDate ?? today);
  const [days, setDays] = useState<number[]>(request.plan?.days ?? initialPlan?.days ?? deliveryDays);
  const [customSlot, setCustomSlot] = useState<Slot>(request.plan?.id === 'custom' && servable(request.plan.slot) ? request.plan.slot : slotOf(servedMeals));
  const [customMealDays, setCustomMealDays] = useState(String(request.plan?.id === 'custom' ? request.plan.mealDays : initialPlan?.mealDays ?? 26));
  const [customPrice, setCustomPrice] = useState(request.plan?.id === 'custom' && request.plan.price !== null ? String(request.plan.price) : '');
  const [mealRules, setMealRules] = useState<SubscriptionMealRules>(request.mealRules ?? request.plan?.mealRules ?? {});
  const [fields, setFields] = useState<Record<string, string>>({});
  const selectedPlan = plans.find((p) => p.id === planId);
  const isCustom = planId === 'custom';
  const customMealDaysNumber = Number(customMealDays);
  const customPriceNumber = Number(customPrice);
  const finalPlanName = isCustom ? `Custom · ${customMealDaysNumber} meal days` : selectedPlan?.name;
  const finalPlanPrice = isCustom ? customPriceNumber : selectedPlan?.price;
  const finalSlot = isCustom ? customSlot : selectedPlan?.slot;
  const finalMealDays = isCustom ? customMealDaysNumber : selectedPlan?.mealDays ?? 0;
  const estimatedEndDate = finalSlot && finalMealDays > 0 && /^\d{4}-\d{2}-\d{2}$/.test(startDate)
    ? computeEndDate({ startDate, mealDays: finalMealDays, slot: finalSlot, days }, { deliveryDays, holidays, meals: servedMeals })
    : undefined;
  const termsChanged = !request.plan || request.plan.id !== planId || request.plan.name !== finalPlanName
    || request.plan.slot !== finalSlot || request.plan.mealDays !== finalMealDays
    || request.plan.price !== finalPlanPrice || request.plan.startDate !== startDate || !sameDays(request.plan.days, days)
    || request.plan.endDate !== estimatedEndDate;
  const asked = mealChoiceLines(request.mealRules ?? request.plan?.mealRules, request.food);
  const canAccept = (!!selectedPlan || isCustom) && !!startDate && days.length > 0 && !!name.trim() && phone.replace(/\D/g, '').length >= 10 && EMAIL.test(email.trim())
    && (!isCustom || (customMealDaysNumber > 0 && customMealDaysNumber <= 365 && customPrice !== '' && customPriceNumber <= 1_000_000));

  useEffect(() => {
    if (error) setFields(fieldsFrom(error));
  }, [error]);

  const submit = () => {
    setFields({});
    onAccept({
      name, phone, email, area, food, notes, planId, startDate, days,
      ...(isCustom ? { customPlan: { slot: customSlot, mealDays: customMealDaysNumber, price: customPriceNumber } } : {}),
      mealRules: finalSlot ? mealRulesToSave(mealsOf(finalSlot), mealRules, food) : undefined,
    });
  };

  return (
    <Card pad>
      <CardTitle title="Review the request" right={<span className="mono">{submittedDate(request.submittedAt)}</span>} />
      {request.plan && (
        <div className="request-box">
          <b>Asked for {request.plan.name}</b>
          <span>{mealsLabel(request.plan.slot)} · {request.plan.mealDays} meal days · {daysLabel(request.plan.days)} · from {formatDay(request.plan.startDate)}</span>
          <span>{request.plan.price !== null ? `Price ${formatInr(request.plan.price)}` : 'Price for you to set'}</span>
          {asked.map((line) => <span key={line} className="request-box-dishes">{line}</span>)}
        </div>
      )}
      <Field label="Name" id="review-name" value={name} onChange={setName} error={fields.name} />
      <Field label="WhatsApp number" id="review-phone" value={phone} onChange={setPhone} prefix="+91" inputMode="tel" error={fields.phone} />
      <Field label="Email" id="review-email" type="email" value={email} onChange={setEmail} autoComplete="email" hint="The final plan is emailed here when you accept." error={fields.email} />
      <Field label="Delivery location" id="review-area" value={area} onChange={setArea} placeholder="Area or neighbourhood" error={fields.area} />
      <div className="stack"><span className="label">Diet</span><DietPicker label="Diet" value={food} onChange={setFood} /></div>
      <Field label="Diet notes" id="review-notes" value={notes} onChange={setNotes} placeholder="Optional" error={fields.notes} />
      <div className="stack">
        <span className="label">Plan</span>
        {plans.map((p) => (
          <RadioCard
            key={p.id}
            title={p.name}
            sub={`${p.mealDays} meal days · ${mealsLabel(p.slot).toLowerCase()} · ${daysLabel(p.days ?? deliveryDays)}`}
            right={formatInr(p.price)}
            on={planId === p.id}
            onClick={() => { setPlanId(p.id); setDays(p.days ?? deliveryDays); }}
          />
        ))}
        <RadioCard
          title="Custom plan"
          sub="Set the meals, days and price"
          right="You set the price"
          on={isCustom}
          onClick={() => {
            if (isCustom) return;
            const current = selectedPlan ?? plans[0];
            if (current) {
              setCustomSlot(servable(current.slot) ? current.slot : slotOf(servedMeals));
              setCustomMealDays(String(current.mealDays));
              setDays(current.days ?? deliveryDays);
            }
            setCustomPrice('');
            setPlanId('custom');
          }}
        />
      </div>
      {isCustom && (
        <>
          <div className="stack">
            <span className="label">Meals each day</span>
            <MealPicker label="Custom plan meals" value={customSlot} allowed={servedMeals} onChange={setCustomSlot} />
          </div>
          <div className="two">
            <Field label="Meal days" id="review-custom-meal-days" value={customMealDays} onChange={(v) => setCustomMealDays(v.replace(/\D/g, ''))} inputMode="numeric" error={fields['customPlan.mealDays']} />
            <Field label="Price" id="review-custom-price" value={customPrice} onChange={(v) => setCustomPrice(v.replace(/\D/g, ''))} prefix="₹" inputMode="numeric" error={fields['customPlan.price']} />
          </div>
        </>
      )}
      <div className="stack">
        <span className="label">Delivery days</span>
        <DayPicker label="Delivery days" value={days} allowed={isCustom ? undefined : selectedPlan?.days ?? deliveryDays} onChange={setDays} />
        {fields.days && <span className="hint bad">{fields.days}</span>}
      </div>
      <Field label="Start date" id="review-start-date" type="date" value={startDate} onChange={setStartDate} min={today} error={fields.startDate} />

      {dishes.length > 0 && finalSlot && (
        <MealChoiceSection
          slot={finalSlot}
          mealRules={mealRules}
          onChangeMealRules={setMealRules}
          dishes={dishes}
          customerFood={food}
          helperText="Check or change what they chose before the plan starts."
        />
      )}

      <Helper>Accepting starts the plan with the terms set here and emails them the details. They don’t need to approve changes.</Helper>
      <ErrorText>{error && !Object.keys(fields).length ? errorMessage(error) : ''}</ErrorText>
      <BtnPrimary label={termsChanged ? 'Accept with these changes' : 'Accept and start'} icon="check" busy={busy} onClick={submit} disabled={!canAccept} />
      <BtnSecondary label="Dismiss request" danger busy={busy} onClick={onDismiss} />
    </Card>
  );
}

export function CustomerRegistration() {
  const { token = '' } = useParams();
  const form = usePublicRegistration(token);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [area, setArea] = useState('');
  const [food, setFood] = useState<Food>('veg');
  const [notes, setNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [slot, setSlot] = useState<Slot | ''>('');
  const [mealDays, setMealDays] = useState('');
  const [forceCustom, setForceCustom] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [mealRules, setMealRules] = useState<SubscriptionMealRules>({});
  const f = form.data;

  useEffect(() => {
    if (!f) return;
    const firstPlan = f.plans[0];
    setStartDate((cur) => cur || f.today);
    setSlot((cur) => cur || firstPlan?.slot || slotOf(f.servedMeals));
    setMealDays((cur) => cur || String(firstPlan?.mealDays ?? 26));
    setDays((cur) => (cur.length ? cur : firstPlan?.days ?? f.deliveryDays));
  }, [f]);

  const md = Number(mealDays);
  // The plan they picked, while what they set still matches it; anything else is a custom request.
  const matchingPlan = !forceCustom && f ? f.plans.find((p) => p.slot === slot && p.mealDays === md && sameDays(p.days ?? f.deliveryDays, days)) : undefined;
  const valid = !!f && !!slot && md >= 1 && md <= 365 && days.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(startDate);
  const endDate = valid ? computeEndDate({ startDate, mealDays: md, slot, days }, { deliveryDays: f.deliveryDays, holidays: f.holidays, meals: f.servedMeals }) : null;
  const rules = slot ? mealRulesToSave(mealsOf(slot), mealRules, food) : undefined;
  const canSubmit = valid && startDate >= f.today && mealsOf(slot).some((m) => f.servedMeals.includes(m))
    && !!name.trim() && phone.replace(/\D/g, '').length >= 10 && EMAIL.test(email.trim());

  const submit = useMutation({
    mutationFn: (input: CustomerRegistrationInput) => post<RegistrationSubmittedDto>(`/registration/${token}`, input),
    onSuccess: () => setDone(true),
    onError: (e) => setFields(fieldsFrom(e)),
  });

  return (
    <main className="main task">
      <div className="public-logo"><Logo size={18} /></div>
      {form.isPending ? <PageLoader label="Opening the form" /> : form.error || !f ? (
        <><TaskTitle title="This link isn’t working" sub={errorMessage(form.error)} /><BtnSecondary label="Back" onClick={() => history.back()} /></>
      ) : done ? (
        <>
          <TaskTitle title="Sent" sub={`${f.kitchenName} will look at your request.`} />
          <Card pad><Empty title="Thank you">The kitchen confirms the plan and price, and emails you the details. Nothing else to do.</Empty></Card>
        </>
      ) : (
        <>
          <TaskTitle title={`Join ${f.kitchenName}`} sub="Choose your plan and days. The kitchen checks everything before it starts." />
          <Card pad>
            <Field label="Your name" id="customer-name" value={name} onChange={setName} autoComplete="name" error={fields.name} />
            <Field label="WhatsApp number" id="customer-phone" value={phone} onChange={setPhone} prefix="+91" inputMode="tel" autoComplete="tel-national" error={fields.phone} />
            <Field label="Email" id="customer-email" type="email" value={email} onChange={setEmail} autoComplete="email" hint="The kitchen’s answer and your plan come here." error={fields.email} />
            <Field label="Delivery location" id="customer-area" value={area} onChange={setArea} placeholder="Area or neighbourhood" error={fields.area} />
            <div className="stack"><span className="label">Diet</span><DietPicker label="Diet" value={food} onChange={setFood} /></div>
            <Field label="Diet notes" id="customer-notes" value={notes} onChange={setNotes} placeholder="Optional, such as no onion" error={fields.notes} />
          </Card>
          <Card pad>
            <CardTitle title="Choose a plan" />
            {f.plans.map((p) => (
              <RadioCard
                key={p.id}
                title={p.name}
                sub={`${p.mealDays} meal days · ${mealsLabel(p.slot).toLowerCase()} · ${daysLabel(p.days ?? f.deliveryDays)}`}
                right={formatInr(p.price)}
                on={matchingPlan?.id === p.id}
                onClick={() => { setForceCustom(false); setSlot(p.slot); setMealDays(String(p.mealDays)); setDays(p.days ?? f.deliveryDays); }}
              />
            ))}
            <RadioCard title="Custom plan" sub="Pick your meals and days. The kitchen sets the price." on={!matchingPlan} onClick={() => setForceCustom(true)} />
            <div className="stack">
              <span className="label">Meals each day</span>
              {slot && <MealPicker label="Plan meals" value={slot} allowed={f.servedMeals} onChange={(s) => { setSlot(s); setForceCustom(false); }} />}
            </div>
            <Field label="Meal days" id="customer-meal-days" value={mealDays} onChange={(v) => { setMealDays(v.replace(/\D/g, '')); setForceCustom(false); }} inputMode="numeric" error={fields['customPlan.mealDays']} />
            <Helper>Meal days are the days you get meals. Below, the weekdays you want them on.</Helper>
            <div className="stack">
              <span className="label">Delivery days</span>
              <DayPicker label="Delivery days" value={days} allowed={f.deliveryDays} onChange={(d) => { setDays(d); setForceCustom(false); }} />
              {fields.days && <span className="hint bad">{fields.days}</span>}
            </div>
            <Field label="Start date" id="customer-start-date" type="date" value={startDate} onChange={setStartDate} min={f.today} error={fields.startDate} />
            <Helper>{matchingPlan ? 'The kitchen confirms your plan when it looks at your request.' : 'The kitchen sets the price when it looks at your request.'}</Helper>
            {fields.planId && <ErrorText>{fields.planId}</ErrorText>}
          </Card>

          {f.dishes.length > 0 && slot && (
            <MealChoiceSection
              slot={slot}
              mealRules={mealRules}
              onChangeMealRules={setMealRules}
              dishes={f.dishes}
              customerFood={food}
              onChangeCustomerFood={setFood}
              helperText="Pick dishes, or keep the day’s special."
            />
          )}

          <Hero label="Plan summary">
            <div className="between"><Mono muted>Plan summary</Mono>{valid && <Mono saffron>{matchingPlan ? formatInr(matchingPlan.price) : 'Price on review'}</Mono>}</div>
            {valid ? (
              <div className="plan-sum">
                <span className="plan-sum-length">{matchingPlan ? matchingPlan.name : `${md} ${planUnit(slot, md)}`}</span>
                <span className="plan-sum-dates">{formatDay(startDate)}<Icon name="arrow" size={16} sw={2.4} color="var(--saffron)" />{endDate ? formatDay(endDate) : '—'}</span>
                <span className="plan-sum-days">{mealsLabel(slot)} · {daysLabel(days)} · {totalMeals({ mealDays: md, slot })} meals</span>
                {mealChoiceLines(rules, food).map((line) => <span key={line} className="plan-sum-dishes">{line}</span>)}
              </div>
            ) : <p>Choose a plan and start date to see your dates.</p>}
            <p>The kitchen confirms your plan and payment when it accepts.</p>
          </Hero>

          <ErrorText>{submit.error && !Object.keys(fields).length ? errorMessage(submit.error) : ''}</ErrorText>
          <BtnPrimary
            label="Send for approval"
            icon="send"
            busy={submit.isPending}
            disabled={!canSubmit}
            onClick={() => {
              setFields({});
              submit.mutate({
                name, phone, email, area, food, notes, planId: matchingPlan?.id ?? 'custom', startDate, days,
                ...(matchingPlan ? {} : { customPlan: { slot: slot as Slot, mealDays: md } }),
                mealRules: rules,
              });
            }}
          />
          <p className="sub center">No account needed</p>
        </>
      )}
    </main>
  );
}
